import { toast } from 'sonner'
import { format } from 'date-fns'
import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { ChevronDownIcon } from 'lucide-react'
import axios, { endpoints } from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import type { OrderPrivilege } from './order-privilege.types'

import { SHIPPING_STATUS_OPTIONS } from './helper'
import { ShippingStatus } from './order-privilege.types'

interface OrderShippingStatusDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  order: OrderPrivilege | null
  onSaved?: () => void
}

export default function OrderShippingStatusDialog({
  open,
  onOpenChange,
  order,
  onSaved,
}: OrderShippingStatusDialogProps) {
  const [saving, setSaving] = useState(false)
  const [shippingStatus, setShippingStatus] = useState<
    ShippingStatus | undefined
  >()
  const [trackingNumber, setTrackingNumber] = useState('')
  const [shippedAt, setShippedAt] = useState<Date | undefined>()
  const [timeValue, setTimeValue] = useState('')
  const [shippingNote, setShippingNote] = useState('')

  useEffect(() => {
    if (!order) return
    setShippingStatus(order.shippingStatus)
    setTrackingNumber(order.trackingNumber || '')
    if (order.shippedAt) {
      const date = new Date(order.shippedAt)
      setShippedAt(date)
      setTimeValue(date.toTimeString().slice(0, 5))
    } else {
      setShippedAt(undefined)
      setTimeValue('')
    }
    setShippingNote(order.shippingNote || '')
  }, [order])

  const handleSave = async () => {
    if (!order || saving) return
    try {
      setSaving(true)
      await axios.patch(
        endpoints.orderPrivileges.updateShippingStatus(order.id),
        {
          shippingStatus,
          trackingNumber: trackingNumber || '',
          shippedAt:
            shippedAt && timeValue
              ? new Date(
                  `${shippedAt.toDateString()} ${timeValue}`,
                ).toISOString()
              : new Date().toISOString(),
          shippingNote: shippingNote || '',
        },
      )
      toast.success('อัปเดตสถานะสำเร็จ')
      onOpenChange(false)
      onSaved?.()
    } catch {
      toast.error('อัปเดตสถานะไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>อัปเดตสถานะออเดอร์</DialogTitle>
        </DialogHeader>

        {order && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1 w-full">
                <div className="text-sm text-muted-foreground">
                  สถานะการจัดส่ง
                </div>
                <Select
                  value={shippingStatus}
                  onValueChange={(v) => setShippingStatus(v as ShippingStatus)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="เลือกสถานะการจัดส่ง" />
                  </SelectTrigger>
                  <SelectContent>
                    {SHIPPING_STATUS_OPTIONS.filter(
                      (o) => o.value !== 'all',
                    ).map((opt) => (
                      <SelectItem key={opt.value} value={String(opt.value)}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-4">
                <div className="space-y-1">
                  <div className="text-sm text-muted-foreground">
                    วันที่จัดส่ง
                  </div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        id="date-picker"
                        className="w-32 justify-between font-normal"
                      >
                        {shippedAt
                          ? format(shippedAt, 'dd/MM/yyyy')
                          : 'เลือกวันที่'}
                        <ChevronDownIcon />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto overflow-hidden p-0"
                      align="start"
                    >
                      <Calendar
                        mode="single"
                        selected={shippedAt}
                        captionLayout="dropdown"
                        onSelect={(date) => {
                          setShippedAt(date)
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1">
                  <div className="text-sm text-muted-foreground">
                    เวลาจัดส่ง
                  </div>

                  <Input
                    type="time"
                    id="time-picker"
                    step="60"
                    value={timeValue}
                    onChange={(e) => setTimeValue(e.target.value)}
                    className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">เลขพัสดุ</div>
              <Input
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="ระบุเลขพัสดุ"
              />
            </div>

            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">
                หมายเหตุการจัดส่ง
              </div>
              <Textarea
                value={shippingNote}
                onChange={(e) => setShippingNote(e.target.value)}
                placeholder="ระบุหมายเหตุการจัดส่ง"
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                ยกเลิก
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                บันทึก
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
